import { css, IconButton as MuiIconButton, styled } from '@mui/material';

import { iconButtonGhostMiniClasses } from './theme';

import type { Theme } from '../../types';
import type { IconButtonGhostMiniOwnerState, IconButtonGhostMiniProps } from './types';

const IconButtonGhostMini = (props: IconButtonGhostMiniProps) => {
  // 기본값을 컴포넌트 내부에서 직접 처리 (theme defaultProps에서 이동)
  const {
    children,
    disabled = false,
    color = 'default',
    size = 'sm',
    type = 'circle',
    ...others
  } = props;

  const ownerState: IconButtonGhostMiniOwnerState = {
    ...props,
    disabled,
    color,
    size,
    type,
  };

  return (
    <IconButtonGhostMiniRoot
      disabled={disabled}
      ownerState={ownerState}
      disableRipple
      disableFocusRipple
      {...others}
    >
      <IconButtonGhostMiniIcon ownerState={ownerState}>{children}</IconButtonGhostMiniIcon>
    </IconButtonGhostMiniRoot>
  );
};
IconButtonGhostMini.displayName = 'IconButtonGhostMini';

// name, slot 제거, target 유지
// shouldForwardProp에서 커스텀 props 제외 (size, color, type)
const IconButtonGhostMiniRoot = styled(MuiIconButton, {
  target: iconButtonGhostMiniClasses.root,
  shouldForwardProp: (prop: PropertyKey) =>
    !['ownerState', 'size', 'color', 'type'].includes(prop as string),
})<{ ownerState: IconButtonGhostMiniOwnerState }>(({ ownerState, theme }) => {
  const { color, size, type } = ownerState;

  return css`
    /* theme.ts의 root styleOverrides에서 이동 */
    display: inline-flex;
    align-items: center;
    padding: 0;

    color: ${theme.color['icon/default']};
    cursor: pointer;

    &:hover {
      color: ${theme.color['icon/weakest/hover']}; /* theme 최신화 필요 : icon.hover */
    }

    &:disabled {
      color: ${color === 'inverse'
        ? theme.color['icon/inverse/disabled']
        : theme.color['icon/disabled']};
      pointer-events: none;
      cursor: not-allowed;
    }

    &:focus-visible {
      position: relative;
      ${theme.mixins.focusRing('inside', {
        borderRadius: type === 'circle' ? 50 : 1,
        thickness: 1,
        selectors: { focus: '&' },
      })};
    }

    /* theme.ts의 variants에서 이동 */
    ${getSizeStyles(size)}
    ${getTypeStyles(type, size, theme)}
    ${getColorStyles(color, type, theme)}
  `;
});

const IconButtonGhostMiniIcon = styled('div', {
  target: iconButtonGhostMiniClasses.icon,
})<{ ownerState: IconButtonGhostMiniOwnerState }>`
  /* theme.ts의 icon styleOverrides에서 이동 */
  display: flex;
  width: 1em;
  height: 1em;
`;

// 스타일 헬퍼 함수들 (theme.ts의 variants에서 이동)
const getSizeStyles = (size: IconButtonGhostMiniOwnerState['size']) => {
  const sizeStylesMap = {
    sm: css`
      width: 12px;
      height: 12px;
      font-size: 12px;
    `,
    md: css`
      width: 16px;
      height: 16px;
      font-size: 16px;
    `,
    lg: css`
      width: 20px;
      height: 20px;
      font-size: 20px;
    `,
  };

  return sizeStylesMap[size] || css``;
};

const getTypeStyles = (
  type: IconButtonGhostMiniOwnerState['type'],
  size: IconButtonGhostMiniOwnerState['size'],
  theme: Theme,
) => {
  if (type === 'circle') {
    return css``;
  }

  // type === 'square'
  return css`
    border-radius: 0;
    padding: ${size === 'sm' ? '0px' : '2px' /* theme.spacing[2] */};
    font-size: ${size === 'lg' ? '16px' : '12px'};

    &:hover {
      background-color: ${theme.color['actionSurface/ghost/hover']};
    }
    &:active {
      background-color: ${theme.color['actionSurface/ghost/press']};
    }
  `;
};

const getColorStyles = (
  color: IconButtonGhostMiniOwnerState['color'],
  type: IconButtonGhostMiniOwnerState['type'],
  theme: Theme,
) => {
  const colorStylesMap = {
    default: css``,
    weak: css`
      color: ${theme.color['icon/weak/default']};
      &:hover {
        color: ${theme.color['icon/weak/hover']};
      }
      &:active {
        color: ${theme.color['icon/weak/hover']};
      }
    `,
    weaker: css`
      color: ${theme.color['icon/weaker/default']};
      &:hover {
        color: ${theme.color['icon/weaker/hover']};
      }
      &:active {
        color: ${theme.color['icon/weaker/hover']};
      }
    `,
    weakest: css`
      color: ${theme.color['icon/weakest/default']};
      &:hover {
        color: ${theme.color['icon/weakest/hover']};
      }
      &:active {
        color: ${theme.color['icon/weakest/hover']};
      }
    `,
    strong: css`
      color: ${theme.color['icon/strong/default']};
      &:hover {
        color: ${theme.color['icon/strong/hover']};
      }
      &:active {
        color: ${theme.color['icon/strong/hover']};
      }
    `,
    global: css`
      color: ${theme.color['icon/global/weakest/default']};
      &:hover {
        color: ${theme.color['icon/global/weakest/hover']};
      }
      &:active {
        color: ${theme.color['icon/global/weakest/hover']};
      }
    `,
    inverse:
      type === 'square'
        ? css`
            color: ${theme.color['icon/inverse/default']};

            &:hover {
              color: ${theme.color['icon/inverse/hover']};
              background-color: ${theme.color['actionSurface/ghostInverse/hover']};
            }
            &:active {
              color: ${theme.color['icon/inverse/press']};
              background-color: ${theme.color['actionSurface/ghostInverse/press']};
            }
          `
        : css`
            color: ${theme.color['icon/inverse/default']};

            &:hover {
              color: ${theme.color['icon/inverse/hover']};
            }
            &:active {
              color: ${theme.color['icon/inverse/press']};
            }
          `,
  };

  return colorStylesMap[color] || css``;
};

export default IconButtonGhostMini;
